/**
 * Meeting filters and search component
 */

import {useState, useEffect, useRef} from 'react';
import {useMeetingsStore} from '@voxapilot/shared';
import {Button} from '../ui/Button';
import {cn} from '../../utils/cn';
import type {MeetingStatus} from '@voxapilot/shared';

export function MeetingFilters() {
  const {filters, setFilters} = useMeetingsStore();
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const statuses: MeetingStatus[] = ['draft', 'recording', 'processing', 'completed', 'archived'];

  // Debounce search input - update filters after 300ms of no typing
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      // Merge with current filters
      setFilters({
        ...filters,
        search: searchQuery || undefined,
        offset: 0,
      });
    }, 300);

    // Cleanup on unmount or when searchQuery changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, filters, setFilters]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Filters will be updated via useEffect debounce
  };

  const handleStatusFilter = (status: MeetingStatus | undefined) => {
    setFilters({...filters, status, offset: 0});
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({limit: 20, offset: 0});
  };

  const hasActiveFilters = filters.status || filters.search || filters.startDate || filters.endDate;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 space-y-4">
      {/* Search */}
      <div>
        <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Search
        </label>
        <input
          id="search"
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search meetings by title or notes..."
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Status Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Status
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'px-3 py-1 rounded-md text-sm font-medium transition-colors',
              !filters.status
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            All
          </button>
          {statuses.map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <div>
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
}

